import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Skeleton,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  IconButton,
  Collapse,
} from '@chakra-ui/react';
import {
  MdRefresh,
  MdSearch,
  MdBusiness,
  MdAccountBalance,
  MdOpenInNew,
  MdCreditCard,
  MdSavings,
  MdWarning,
  MdCheckCircle,
  MdSwitchAccount,
  MdExpandMore,
  MdExpandLess,
  MdChevronLeft,
  MdChevronRight,
  MdArrowUpward,
  MdArrowDownward,
  MdUnfoldMore,
} from 'react-icons/md';
import { useConnectedAccounts } from '../../hooks/useConnectedAccounts';
import { TenantAccountEntry, ConnectedBank } from '../../types/admin';
import { useSupportMode } from '../../shared/support-mode';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatBalance(amount: number | null, currency: string | null): string {
  if (amount === null) return '—';
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: currency ?? 'USD',
    maximumFractionDigits: 0,
  });
}

function tokenStatusColor(status: string): string {
  switch (status) {
    case 'ACTIVE': return 'green';
    case 'LOGIN_REQUIRED': return 'orange';
    case 'UNKNOWN': return 'yellow';
    case 'REVOKED': return 'red';
    default: return 'gray';
  }
}

function accountTypeIcon(type: string): React.ElementType {
  if (type === 'credit') return MdCreditCard;
  if (type === 'depository') return MdSavings;
  return MdAccountBalance;
}

// ─── BankRow ──────────────────────────────────────────────────────────────────

interface BankRowProps { bank: ConnectedBank }

const BankRow: React.FC<BankRowProps> = ({ bank }) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const bankBg = useColorModeValue('gray.50', 'gray.750');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const totalBalance = bank.accounts.reduce(
    (sum, a) => (a.currentBalance !== null ? sum + a.currentBalance : sum),
    0,
  );
  const currency = bank.accounts.find((a) => a.isoCurrencyCode)?.isoCurrencyCode ?? null;
  const hasBalance = bank.accounts.some((a) => a.currentBalance !== null);

  const typeCount = bank.accounts.reduce<Record<string, number>>((acc, a) => {
    acc[a.type] = (acc[a.type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <Box
      bg={bankBg}
      border="1px"
      borderColor={borderColor}
      borderRadius="md"
      px={3}
      py={2}
    >
      <HStack justify="space-between" mb={bank.accounts.length > 0 ? 1 : 0}>
        <HStack spacing={2} minW={0}>
          <Icon as={MdAccountBalance} color={mutedColor} boxSize={3.5} flexShrink={0} />
          <Text fontSize="xs" fontWeight="semibold" noOfLines={1}>
            {bank.institutionName ?? bank.institutionId ?? 'Unknown'}
          </Text>
        </HStack>
        <Badge
          colorScheme={tokenStatusColor(bank.status)}
          variant="subtle"
          fontSize="xs"
          flexShrink={0}
        >
          {bank.status.replace('_', ' ')}
        </Badge>
      </HStack>

      {bank.accounts.length > 0 && (
        <HStack spacing={3} mt={1} flexWrap="wrap">
          <HStack spacing={1}>
            {Object.entries(typeCount).map(([type, count]) => (
              <HStack key={type} spacing={0.5}>
                <Icon as={accountTypeIcon(type)} color={mutedColor} boxSize={3} />
                <Text fontSize="xs" color={mutedColor}>{count}</Text>
              </HStack>
            ))}
          </HStack>
          <Text fontSize="xs" color={mutedColor}>
            {bank.accounts.length} account{bank.accounts.length !== 1 ? 's' : ''}
          </Text>
          {hasBalance && (
            <Text fontSize="xs" fontFamily="mono" color={mutedColor} ml="auto">
              {formatBalance(totalBalance, currency)}
            </Text>
          )}
        </HStack>
      )}
    </Box>
  );
};

// ─── TenantRow ────────────────────────────────────────────────────────────────

interface TenantRowProps {
  entry: TenantAccountEntry;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
  onSupportMode: () => void;
}

const TenantRow: React.FC<TenantRowProps> = ({
  entry, isExpanded, onToggle, onNavigate, onSupportMode,
}) => {
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');
  const expandedBg = useColorModeValue('blue.50', 'blue.900');

  const hasIssues = entry.banks.some((b) => b.status !== 'ACTIVE');
  const issueCount = entry.banks.filter((b) => b.status !== 'ACTIVE').length;

  return (
    <Tr
      bg={isExpanded ? expandedBg : undefined}
      _hover={{ bg: isExpanded ? expandedBg : hoverBg }}
      cursor="pointer"
      onClick={onToggle}
    >
      {/* Expand toggle */}
      <Td width="40px" onClick={(e) => { e.stopPropagation(); onToggle(); }}>
        <Icon
          as={isExpanded ? MdExpandLess : MdExpandMore}
          color={mutedColor}
          boxSize={5}
        />
      </Td>

      {/* Tenant name + slug */}
      <Td>
        <HStack spacing={2}>
          <Icon
            as={MdBusiness}
            color={hasIssues ? 'orange.400' : 'blue.400'}
            boxSize={4}
            flexShrink={0}
          />
          <VStack align="start" spacing={0} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
              {entry.tenantName}
            </Text>
            <Text fontSize="xs" color={mutedColor} fontFamily="mono" noOfLines={1}>
              {entry.tenantSlug}
            </Text>
          </VStack>
        </HStack>
      </Td>

      {/* Status badges */}
      <Td>
        <HStack spacing={1} flexWrap="wrap">
          {!entry.isActive && (
            <Badge colorScheme="red" variant="subtle" fontSize="xs">Inactive</Badge>
          )}
          {hasIssues ? (
            <Tooltip label={`${issueCount} bank${issueCount !== 1 ? 's' : ''} with issues`}>
              <Badge colorScheme="orange" variant="solid" fontSize="xs">
                <HStack spacing={1}>
                  <Icon as={MdWarning} boxSize={3} />
                  <Text>{issueCount} issue{issueCount !== 1 ? 's' : ''}</Text>
                </HStack>
              </Badge>
            </Tooltip>
          ) : entry.banks.length > 0 ? (
            <Badge colorScheme="green" variant="subtle" fontSize="xs">
              <HStack spacing={1}>
                <Icon as={MdCheckCircle} boxSize={3} />
                <Text>OK</Text>
              </HStack>
            </Badge>
          ) : (
            <Badge colorScheme="gray" variant="subtle" fontSize="xs">No banks</Badge>
          )}
        </HStack>
      </Td>

      {/* Banks count */}
      <Td isNumeric>
        <Text fontSize="sm" fontWeight="medium">{entry.totalBanks}</Text>
      </Td>

      {/* Accounts count */}
      <Td isNumeric>
        <Text fontSize="sm" fontWeight="medium">{entry.totalAccounts}</Text>
      </Td>

      {/* Actions */}
      <Td onClick={(e) => e.stopPropagation()}>
        <HStack spacing={2} justify="flex-end">
          <Tooltip label="View tenant details">
            <IconButton
              aria-label="View tenant"
              icon={<Icon as={MdOpenInNew} />}
              size="xs"
              variant="outline"
              colorScheme={hasIssues ? 'orange' : 'blue'}
              onClick={onNavigate}
            />
          </Tooltip>
          <Tooltip label="Enter support mode as this tenant">
            <IconButton
              aria-label="Support mode"
              icon={<Icon as={MdSwitchAccount} />}
              size="xs"
              variant="solid"
              colorScheme="purple"
              onClick={onSupportMode}
            />
          </Tooltip>
        </HStack>
      </Td>
    </Tr>
  );
};

// ─── TenantBanksRow (expanded detail row) ─────────────────────────────────────

interface TenantBanksRowProps {
  entry: TenantAccountEntry;
  isExpanded: boolean;
}

const TenantBanksRow: React.FC<TenantBanksRowProps> = ({ entry, isExpanded }) => {
  const expandedBg = useColorModeValue('blue.50', 'blue.900');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Tr bg={expandedBg}>
      <Td colSpan={6} p={0}>
        <Collapse in={isExpanded} animateOpacity>
          <Box px={8} py={3}>
            {entry.banks.length === 0 ? (
              <Text fontSize="xs" color={mutedColor} fontStyle="italic">
                No banks connected
              </Text>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={2}>
                {entry.banks.map((bank) => (
                  <BankRow key={bank.tokenId} bank={bank} />
                ))}
              </SimpleGrid>
            )}
          </Box>
        </Collapse>
      </Td>
    </Tr>
  );
};

// ─── main page ────────────────────────────────────────────────────────────────

type SortField = 'name' | 'accounts' | 'banks';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const ConnectedAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useConnectedAccounts();
  const { enterSupportMode } = useSupportMode();
  const [search, setSearch] = useState('');
  const [filterHasIssues, setFilterHasIssues] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const theadBg = useColorModeValue('gray.50', 'gray.750');

  // Reset to first page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRows(new Set());
  }, [search, filterHasIssues, pageSize, sortBy, sortOrder]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = [...data.tenants];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (t) =>
          t.tenantName.toLowerCase().includes(q) ||
          t.tenantSlug.toLowerCase().includes(q) ||
          t.banks.some((b) => b.institutionName?.toLowerCase().includes(q)),
      );
    }
    if (filterHasIssues) {
      list = list.filter((t) => t.banks.some((b) => b.status !== 'ACTIVE'));
    }
    list.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.tenantName.localeCompare(b.tenantName);
      else if (sortBy === 'accounts') cmp = a.totalAccounts - b.totalAccounts;
      else if (sortBy === 'banks') cmp = a.totalBanks - b.totalBanks;
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [data, search, filterHasIssues, sortBy, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function toggleRow(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <HStack spacing={2} mb={1}>
            <Icon as={MdAccountBalance} color="blue.400" boxSize={6} />
            <Heading size="lg">Connected Accounts</Heading>
          </HStack>
          <Text color={mutedColor} fontSize="sm">
            All bank connections and accounts per tenant
          </Text>
        </Box>
        <Button
          leftIcon={<Icon as={MdRefresh} />}
          variant="outline"
          size="sm"
          onClick={refetch}
          isLoading={loading}
        >
          Refresh
        </Button>
      </HStack>

      {/* Error */}
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertTitle>Failed to load</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary KPIs */}
      {!loading && data && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {[
            { label: 'Total tenants', value: data.totals.totalTenants, color: 'blue.500' },
            { label: 'With banks', value: data.totals.tenantsWithBanks, color: 'green.500' },
            { label: 'Banks connected', value: data.totals.totalBanks, color: 'purple.500' },
            { label: 'Total accounts', value: data.totals.totalAccounts, color: 'teal.500' },
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              bg={bgColor}
              border="1px"
              borderColor={borderColor}
              borderRadius="lg"
              p={4}
              textAlign="center"
            >
              <Text fontSize="2xl" fontWeight="bold" color={color}>{value}</Text>
              <Text fontSize="xs" color={mutedColor}>{label}</Text>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Skeleton KPIs */}
      {loading && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} h="80px" borderRadius="lg" />)}
        </SimpleGrid>
      )}

      {/* Filters */}
      <HStack spacing={3} wrap="wrap">
        <InputGroup maxW="320px" size="sm">
          <InputLeftElement pointerEvents="none">
            <Icon as={MdSearch} color={mutedColor} />
          </InputLeftElement>
          <Input
            placeholder="Search tenant or bank..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            borderRadius="md"
          />
        </InputGroup>
        <Button
          size="sm"
          variant={filterHasIssues ? 'solid' : 'outline'}
          colorScheme={filterHasIssues ? 'orange' : 'gray'}
          onClick={() => setFilterHasIssues((v) => !v)}
        >
          Issues only
        </Button>
        {(search || filterHasIssues) && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setSearch(''); setFilterHasIssues(false); }}
          >
            Clear
          </Button>
        )}
        <HStack spacing={1}>
          <Text fontSize="sm" color={mutedColor} whiteSpace="nowrap">Sort:</Text>
          <Select
            size="sm"
            width="130px"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortField)}
            borderRadius="md"
          >
            <option value="name">Name</option>
            <option value="accounts">Accounts</option>
            <option value="banks">Banks</option>
          </Select>
          <Tooltip label={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
            <IconButton
              aria-label="Toggle sort order"
              icon={<Icon as={sortOrder === 'asc' ? MdArrowUpward : MdArrowDownward} />}
              size="sm"
              variant="ghost"
              onClick={() => setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))}
            />
          </Tooltip>
        </HStack>
        {data && (
          <Text fontSize="sm" color={mutedColor} ml="auto">
            {filtered.length} of {data.tenants.length} tenants
          </Text>
        )}
      </HStack>

      <Divider />

      {/* Skeleton table */}
      {loading && (
        <VStack spacing={2} align="stretch">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} h="52px" borderRadius="md" />
          ))}
        </VStack>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && data && (
        <Box
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={10}
          textAlign="center"
        >
          <Icon as={MdAccountBalance} color="gray.300" boxSize={12} mb={3} />
          <Heading size="md" mb={2} color={mutedColor}>
            {search || filterHasIssues ? 'No matches found' : 'No tenants yet'}
          </Heading>
          <Text color={mutedColor} fontSize="sm">
            {search || filterHasIssues
              ? 'Try adjusting your search or filters'
              : 'Tenants will appear here once they connect a bank account'}
          </Text>
        </Box>
      )}

      {/* Tenant table */}
      {!loading && filtered.length > 0 && (
        <Box
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          borderRadius="lg"
          overflow="hidden"
        >
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg={theadBg}>
                <Tr>
                  <Th width="40px" />
                  <Th>Tenant</Th>
                  <Th>Status</Th>
                  <Th isNumeric>Banks</Th>
                  <Th isNumeric>Accounts</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {paginated.map((entry) => (
                  <React.Fragment key={entry.tenantId}>
                    <TenantRow
                      entry={entry}
                      isExpanded={expandedRows.has(entry.tenantId)}
                      onToggle={() => toggleRow(entry.tenantId)}
                      onNavigate={() => navigate(`/admin/tenants/${entry.tenantId}`)}
                      onSupportMode={() => {
                        enterSupportMode({
                          id: entry.tenantId,
                          name: entry.tenantName,
                          slug: entry.tenantSlug,
                          managerEmail: entry.managerEmail,
                          isActive: entry.isActive,
                          createdAt: new Date().toISOString(),
                        });
                        navigate('/home');
                      }}
                    />
                    <TenantBanksRow
                      entry={entry}
                      isExpanded={expandedRows.has(entry.tenantId)}
                    />
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box px={4} py={3} borderTop="1px" borderColor={borderColor}>
            <HStack justify="space-between" wrap="wrap" gap={3}>
              {/* Page size selector */}
              <HStack spacing={2}>
                <Text fontSize="sm" color={mutedColor} whiteSpace="nowrap">Rows per page:</Text>
                <Select
                  size="sm"
                  width="80px"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  borderRadius="md"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </Select>
              </HStack>

              {/* Page info + nav */}
              <HStack spacing={2}>
                <Text fontSize="sm" color={mutedColor} whiteSpace="nowrap">
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length}
                </Text>
                <IconButton
                  aria-label="Previous page"
                  icon={<Icon as={MdChevronLeft} />}
                  size="sm"
                  variant="ghost"
                  isDisabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                />
                <HStack spacing={1}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === 'ellipsis' ? (
                        <Text key={`ellipsis-${idx}`} fontSize="sm" color={mutedColor} px={1}>…</Text>
                      ) : (
                        <Button
                          key={item}
                          size="xs"
                          variant={item === currentPage ? 'solid' : 'ghost'}
                          colorScheme={item === currentPage ? 'blue' : 'gray'}
                          onClick={() => setCurrentPage(item as number)}
                          minW="28px"
                        >
                          {item}
                        </Button>
                      )
                    )}
                </HStack>
                <IconButton
                  aria-label="Next page"
                  icon={<Icon as={MdChevronRight} />}
                  size="sm"
                  variant="ghost"
                  isDisabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                />
              </HStack>
            </HStack>
          </Box>
        </Box>
      )}
    </VStack>
  );
};

export default ConnectedAccountsPage;
